import { useCallback, useEffect, useMemo, useState } from 'react';
import { getScopedProviderOptions } from 'api/group';
import { TScopedProviderOption } from 'types/group';

/**
 * providerId → 姓名。
 *
 * 上限的 practice 接口只返回 providerId，姓名头像一律另 join —— 和广告的
 * ByProviderTable 走的是不同来源（那边的接口自带 name）。
 *
 * **必须 includeSelf: true**：owner 自己也是 practice 成员，会出现在上限的成员表里，
 * 漏掉他那一行就只剩 "Provider #1710"。既有的 useScopedProviderOptions 是
 * includeSelf: false（它服务于"挑一个别人来 credential"的场景），不能直接复用。
 *
 * 只在 PracticeLimits 里调用 —— 组件挂载本身就代表「现在需要姓名」，
 * 不再收一个 enabled 开关。
 */
const useProviderNames = (): ((providerId: number) => string) => {
    const [list, setList] = useState<TScopedProviderOption[]>([]);

    const fetchData = useCallback(async () => {
        try {
            const result = await getScopedProviderOptions({ includeSelf: true });
            if (!result.error) {
                setList(result?.data?.data || []);
            }
        } catch (e) {
            console.error(e);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const byId = useMemo(() => {
        const map = new Map<number, string>();
        list.forEach((p) => {
            const name = `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim();
            if (name) {
                map.set(p.providerId, name);
            }
        });
        return map;
    }, [list]);

    // 名字还没回来、或该 provider 不在名单里时给个可识别的兜底，不要渲染成空白
    return useCallback((providerId: number) => byId.get(providerId) || `Provider #${providerId}`, [byId]);
};

export default useProviderNames;
